import React, { useEffect, useState } from 'react';
import { Input, Select, SelectItem } from '@nextui-org/react';
import { Tasks } from 'common/src/utils/types';
import useGeneralStore from '../../../../store/useGeneralStore';
import useUserStore from '../../../../store/useUserStore';
import { getCategories } from 'common/src/api/categories';

// Define a new interface for the Category
interface Category {
  id: number;
  name: string;
  description: string;
  color: string;
  image_url: string;
  icon_url: string;
}

interface TaskInfoColumnProps {
  formData: Partial<Tasks>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Tasks>>>;
  isEditMode: boolean;
}

const TaskInfoColumn: React.FC<TaskInfoColumnProps> = ({ formData, setFormData, isEditMode }) => {
  const { users } = useGeneralStore();
  const { userProfile } = useUserStore();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await getCategories();
      if (error) {
        console.error('Error fetching categories:', error);
      } else {
        setCategories(data as Category[]);
      }
    };

    fetchCategories();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Set default status to "pending" if not already set
  React.useEffect(() => {
    if (!formData.status) {
      setFormData((prev) => ({ ...prev, status: 'pending' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex-1 pl-4 flex flex-col gap-4">
      <p className="text-semibold text-xl">Datos del Task</p>
      <Select
        label="Categoría"
        selectedKeys={formData.category_id ? [formData.category_id.toString()] : []}
        onSelectionChange={(keys) => {
          const selectedCategoryId = Number(Array.from(keys)[0]);
          const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
          setFormData((prev) => ({
            ...prev,
            category_id: selectedCategoryId,
            category: selectedCategory ? selectedCategory.name : '',
          }));
        }}
      >
        {categories.map((category) => (
          <SelectItem key={category.id.toString()} value={category.id.toString()}>
            {category.name}
          </SelectItem>
        ))}
      </Select>
      <Input
        label="Descripción"
        name="description"
        value={formData.description || ''}
        onChange={handleInputChange}
      />
      <Input
        label="Precio sin IVA ni Fee"
        name="base_amount"
        type="number"
        value={formData.base_amount?.toString() || ''}
        onChange={handleInputChange}
      />
      <Select
        label="Status"
        selectedKeys={formData.status ? [formData.status] : ['pending']}
        onSelectionChange={(keys) => {
          const newStatus = Array.from(keys)[0] as Tasks['status'];
          setFormData((prev) => ({ ...prev, status: newStatus }));
        }}
      >
        <SelectItem key="pending" value="pending">
          Pendiente
        </SelectItem>
        <SelectItem key="accepted" value="accepted">
          Aceptado
        </SelectItem>
        <SelectItem key="assigned" value="assigned">
          Asignado
        </SelectItem>
        <SelectItem key="progress" value="progress">
          En Progreso
        </SelectItem>
        <SelectItem key="complete" value="complete">
          Completo
        </SelectItem>
        <SelectItem key="rejected" value="rejected">
          Rechazado
        </SelectItem>
      </Select>
      {isEditMode && userProfile?.role.includes('admin') && (
        <Select
          label="Assigned User"
          placeholder="Select a user"
          selectedKeys={formData.assigned_user_id ? [formData.assigned_user_id.toString()] : []}
          onSelectionChange={(keys) =>
            setFormData((prev) => ({
              ...prev,
              assigned_user_id: Number(Array.from(keys)[0]) || null,
            }))
          }
          items={[
            { id: '', name: 'Not Assigned' },
            ...users.map((user) => ({
              id: user.id.toString(),
              name: `${user.first_name} ${user.last_name}`,
            })),
          ]}
        >
          {(user) => (
            <SelectItem key={user.id} value={user.id}>
              {user.name}
            </SelectItem>
          )}
        </Select>
      )}
    </div>
  );
};

export default TaskInfoColumn;
